import { useCallback, useState } from 'react';
import { useTranslations } from '@/hooks';
import { useGetLinkableShipmentsQuery, useLinkContractShipmentsMutation } from '@/lib/api/linksApi';
import { buildShipmentLinksPayload, isShipmentLinkedToContract } from '@/lib/mappers/links';
import { LinkShipmentsModal, LINK_FLOWS } from './LinkShipmentsModal';
import { EMPTY_LIST, EMPTY_MAP } from '@/lib/utils';

/**
 * Props:
 *   module        route module segment, for the REST save
 *   contractDbId  the contract's own id, the candidates query's key
 *   contractLabel the business contract id shown in the title; a shipment already linked
 *                 to it is greyed out in step 1
 *   detailFields  (unitType) => GENERAL DETAILS rows
 *   open          whether the wizard is shown
 *   onClose       close handler
 */
export const LinkShipmentsForContract = ({
  module,
  contractDbId,
  contractLabel,
  detailFields,
  open,
  onClose,
}) => {
  const t = useTranslations();
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState(EMPTY_MAP);
  const [linkContractShipments, { isLoading }] = useLinkContractShipmentsMutation();
  const { data, isFetching } = useGetLinkableShipmentsQuery(
    { contractId: contractDbId, search, filters },
    { skip: !open || contractDbId == null },
  );

  // TODO: actual behaviour needed — assign more quantity to an already-linked shipment.
  const isAlreadyLinked = useCallback(
    (row) => isShipmentLinkedToContract(row, contractLabel),
    [contractLabel],
  );

  const handleClose = useCallback(() => {
    setSearch('');
    setFilters(EMPTY_MAP);
    onClose?.();
  }, [onClose]);

  const handleSave = useCallback(
    async (entries) => {
      const result = await linkContractShipments({
        module,
        contractId: contractDbId,
        links: buildShipmentLinksPayload(entries),
      });
      if (result.error) return;
      handleClose();
    },
    [linkContractShipments, module, contractDbId, handleClose],
  );

  // Closing unmounts the modal, so nothing it held survives into the next open.
  if (!open) return null;

  return (
    <LinkShipmentsModal
      open={open}
      onClose={handleClose}
      title={t('LINK_SHIPMENTS_TITLE', { contractId: contractLabel ?? '' })}
      detailFields={detailFields}
      shipments={data?.rows ?? EMPTY_LIST}
      loading={isFetching}
      flow={LINK_FLOWS.shipmentsToContract}
      isSaving={isLoading}
      isRowDisabled={isAlreadyLinked}
      onSearchChange={setSearch}
      onFilterChange={setFilters}
      onSave={handleSave}
    />
  );
};
